import {ErrorBoundary} from "react-error-boundary";
import {CreateLiveClassProvider} from "../../context/live-class/create-live-class";
import {MessageProvider} from "../../context/message-context";
import {CreateProductProvider} from "../../context/products/add-product-context";
import {ViewProductProvider} from "../../context/products/view-product-context";
import {GetDiscountCodesProvider} from "../../discounts/useCases/get-discounts/context";
import {GetDiscountCodeProvider} from "../../discounts/useCases/update-discount-code/context";
import {CallsPage} from "../../pages/dashboard/calls";
import {AddCallPage} from "../../pages/dashboard/calls/add";
import {AvailabilityPage} from "../../pages/dashboard/calls/availability";
import {EditBundlePage} from "../../pages/dashboard/calls/bundle/edit";
import {AddBundlePage} from "../../pages/dashboard/calls/bundle/new";
import {EditCallPage} from "../../pages/dashboard/calls/edit";
import {CustomersPage} from "../../pages/dashboard/customers";
import {ViewCustomerDetailsPage} from "../../pages/dashboard/customers/view";
import {DiscountsPage} from "../../pages/dashboard/discounts";
import {EditDiscountCodePage} from "../../pages/dashboard/discounts/edit";
import {CreateDiscountCodePage} from "../../pages/dashboard/discounts/new";
import {ViewDiscountCodePage} from "../../pages/dashboard/discounts/view";
import {GoogleVerify} from "../../pages/dashboard/google/verify";
import {DashboardHomepage} from "../../pages/dashboard/home";
import {LiveClassesPage} from "../../pages/dashboard/live-classes";
import {LiveClassCreatePage} from "../../pages/dashboard/live-classes/new";
import {ViewLiveClassPage} from "../../pages/dashboard/live-classes/view";
import {MessagesPage} from "../../pages/dashboard/messages";
import {StorePage} from "../../pages/dashboard/products";
import {EditProductPage} from "../../pages/dashboard/products/edit";
import {AddNewProductPage} from "../../pages/dashboard/products/new";
import {ViewProductPage} from "../../pages/dashboard/products/view";
import {EditProfilePage} from "../../pages/dashboard/profile/edit-profile";
import {RecordingsPage} from "../../pages/dashboard/recordings";
import {SettingPage} from "../../pages/dashboard/settings";
import {Alert, Breadcrumb, Button} from "antd";
import {DashboardWrapper} from "../../layouts/dashboard-wrapper";
import {StyledPageHeader} from "../../styles/dashboard/calls/calls";
import {Link, Navigate} from "react-router-dom";
import PayoutV2Page from "../../pages/dashboard/payouts/page";
import {TransactionsPage} from "../../pages/dashboard/transactions";
import {GetSessionProvider} from "../../calls/useCases/editService/context/get-session";
import {GetBundleProvider} from "../../calls/useCases/editBundle/context/get-bundle";
import {GetProductProvider} from "../../products/useCases/edit-product/context/get-product";
import {GetLiveEventProvider} from "../../liveEvents/useCases/edit-live-event/context/get-live-event";
import {GetCoachUpcomingCallsProvider} from "../../context/upcoming-calls/coach";
import ReviewsPage from "../../pages/dashboard/reviews";
import NewOfferingPage from "../../pages/new-offering";
import {ServiceCreationPage} from "../../pages/dashboard/products/services/new";
import {SubscriptionCreationPage} from "../../pages/dashboard/products/subscriptions/new";
import {EditSubscriptionPage} from "../../pages/dashboard/products/subscriptions/edit";
import {SubscriptionSalesInsightsRedirect} from "../../pages/dashboard/products/subscriptions/subscription-sales-insights-redirect";
import {GetEditableSubscriptionProvider} from "../../subscriptions-feature/useCases/edit-subscription/context/get-editable-subscription";
import {CreateStoreServiceProvider} from "../../service-feature/useCases/create-service/context/create-service";
import {EditStoreServicePage} from "../../pages/dashboard/products/services/edit";
import {GetStoreServiceProvider} from "../../service-feature/useCases/edit-service/context/get-store-service";
import {AutomationsPage} from "../../pages/dashboard/automations";
import {EmailAutomationsPage} from "../../pages/dashboard/automations/email";
import {GoogleMeetIntegrationPage} from "../../pages/dashboard/automations/integrations/google-meet";
import {ZoomIntegrationPage} from "../../pages/dashboard/automations/integrations/zoom";
import {ZoomIntegrationCallbackPage} from "../../pages/dashboard/automations/integrations/zoom/callback";
import {TelegramIntegrationPage} from "../../pages/dashboard/automations/integrations/telegram";
import {TiktokPixelIntegrationPage} from "../../pages/dashboard/automations/integrations/tiktok";
import {GoogleCalendarIntegrationPage} from "../../pages/dashboard/automations/integrations/google-calendar";
import {FacebookPixelIntegrationPage} from "../../pages/dashboard/automations/integrations/facebook-pixel";
import {PublicProfilePageBuilder} from "../../public-profile-page";
import {
  IS_INTEGRATIONS_FEATURE_ENABLED,
  IS_PUBLIC_PROFILE_BUILDER_ENABLED,
  IS_TELEGRAM_INTEGRATION_ENABLED,
} from "../../services/config";

type TDashboardPages = {
  path: string;
  element: React.ReactNode;
}[];

export const DashboardRoutes: TDashboardPages = [
  {
    path: "/home",
    element: <DashboardHomepage />,
  },
  {
    path: "/calls",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"1:1 Sessions"}>
            <StyledPageHeader>1:1 Sessions</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetCoachUpcomingCallsProvider>
          <CallsPage />
        </GetCoachUpcomingCallsProvider>
      </ErrorBoundary>
    ),
  },
  // {
  //   path: "/checkout-form",
  //   element: <CheckoutFormPage />,
  // },
  {
    path: "/messages",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Messages"}>
            <StyledPageHeader>Messages</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <MessageProvider>
          <MessagesPage />
        </MessageProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/settings",
    element: <SettingPage />,
  },
  {
    path: "/transactions",
    element: <TransactionsPage />,
  },
  {
    path: "/products",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Store"}>
            <StyledPageHeader>Store</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <StorePage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/new",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Digital Products"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/products">Store</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Create a new product</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <CreateProductProvider>
          <AddNewProductPage />
        </CreateProductProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Store"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/products">Store</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Edit product</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetProductProvider>
          <EditProductPage />
        </GetProductProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/view/:id",
    element: (
      <ViewProductProvider>
        <ViewProductPage />
      </ViewProductProvider>
    ),
  },
  {
    path: "/products/subscriptions/new",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Create a new subscription"}>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <SubscriptionCreationPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/subscriptions/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle="Edit subscription">
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetEditableSubscriptionProvider>
          <EditSubscriptionPage />
        </GetEditableSubscriptionProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/subscriptions/sales-and-insights/:id",
    element: <SubscriptionSalesInsightsRedirect />,
  },
  {
    path: "products/services/new",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Create a new service"}>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <CreateStoreServiceProvider>
          <ServiceCreationPage />
        </CreateStoreServiceProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/products/services/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Store"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/products">Store</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Edit service</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetStoreServiceProvider>
          <EditStoreServicePage />
        </GetStoreServiceProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/calls/availability",
    element: <AvailabilityPage />,
  },
  // {
  //   path: "/calls/availability/:tabKey",
  //   element: <AvailabilityPage />,
  // },
  {path: "/calls/new", element: <AddCallPage />},
  {
    path: "/calls/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Edit a session"}>
            <div>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetSessionProvider>
          <EditCallPage />
        </GetSessionProvider>
      </ErrorBoundary>
    ),
  },
  {path: "/calls/bundle/new", element: <AddBundlePage />},
  {
    path: "/calls/bundle/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Coaching Bundle"}>
            <div>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetBundleProvider>
          <EditBundlePage />
        </GetBundleProvider>
      </ErrorBoundary>
    ),
  },
  {path: "/profile", element: <EditProfilePage />},
  {
    path: "/public-profile",
    element: IS_PUBLIC_PROFILE_BUILDER_ENABLED ? (
      <PublicProfilePageBuilder />
    ) : (
      <Navigate to="/profile" replace />
    ),
  },
  {
    path: "/live-classes",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Live events"}>
            <StyledPageHeader>Live events</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <LiveClassesPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/live-classes/new",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Live events"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/products">Live events</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Create a new live event</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <CreateLiveClassProvider>
          <LiveClassCreatePage />
        </CreateLiveClassProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/live-classes/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Live events"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/products">Live events</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Edit live event</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetLiveEventProvider>
          <ViewLiveClassPage />
        </GetLiveEventProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/recordings",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Recordings"}>
            <StyledPageHeader>Recordings</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <RecordingsPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Automations"}>
            <StyledPageHeader>Automations</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <AutomationsPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations/email",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle="Email automations">
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <EmailAutomationsPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations/integrations/google-calendar",
    element: <GoogleCalendarIntegrationPage />,
  },
  {
    path: "/automations/integrations/google-meet",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle="Google meet">
            <StyledPageHeader>Automations</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GoogleMeetIntegrationPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations/integrations/google-calendar",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle="Google calendar">
            <StyledPageHeader>Automations</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GoogleCalendarIntegrationPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations/integrations/facebook-pixel",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle="Facebook (Meta) pixel">
            <StyledPageHeader>Automations</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <FacebookPixelIntegrationPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/automations/integrations/zoom",
    element: IS_INTEGRATIONS_FEATURE_ENABLED ? (
      <ZoomIntegrationPage />
    ) : (
      <Navigate to="/home" replace />
    ),
  },
  {
    // Zoom OAuth redirect target (configured as the backend's redirect_uri).
    path: "/integrations/zoom/callback",
    element: IS_INTEGRATIONS_FEATURE_ENABLED ? (
      <ZoomIntegrationCallbackPage />
    ) : (
      <Navigate to="/home" replace />
    ),
  },
  {
    path: "/automations/integrations/telegram",
    element: IS_TELEGRAM_INTEGRATION_ENABLED ? (
      <TelegramIntegrationPage />
    ) : (
      <Navigate to="/home" replace />
    ),
  },
  {
    path: "/automations/integrations/tiktok",
    element: <TiktokPixelIntegrationPage />,
  },
  {path: "/customers", element: <CustomersPage />},
  {path: "/customers/view/:id", element: <ViewCustomerDetailsPage />},
  {
    path: "/payouts",
    element: (
      // <ErrorBoundary
      //   FallbackComponent={({error, resetErrorBoundary}) => (
      //     <DashboardWrapper pageTitle={"Payouts"}>
      //       <StyledPageHeader>Payouts</StyledPageHeader>
      //       <div style={{marginTop: "1.8rem"}}>
      //         <Alert
      //           message="Sorry, something went wrong."
      //           showIcon
      //           description={error.message}
      //           type="error"
      //           action={
      //             <Button
      //               onClick={() => {
      //                 resetErrorBoundary();
      //               }}
      //               size="small"
      //               danger
      //             >
      //               Reset
      //             </Button>
      //           }
      //         />
      //       </div>
      //     </DashboardWrapper>
      //   )}
      // >
      //   <FetchAccountDetailsProvider>
      //     <PayoutSettingsProvider>
      //       <PayoutsPage />
      //     </PayoutSettingsProvider>
      //   </FetchAccountDetailsProvider>
      // </ErrorBoundary>
      <PayoutV2Page />
    ),
  },
  {path: "/google/verify", element: <GoogleVerify />},
  {
    path: "/discounts",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Discounts"}>
            <StyledPageHeader>Discounts</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetDiscountCodesProvider>
          <DiscountsPage />
        </GetDiscountCodesProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/discounts/new",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Discounts"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/discounts">Discounts</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Create new discount</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetDiscountCodesProvider>
          <CreateDiscountCodePage />
        </GetDiscountCodesProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/discounts/edit/:id",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Discounts"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/discounts">Discounts</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Edit discount code</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetDiscountCodeProvider>
          <EditDiscountCodePage />
        </GetDiscountCodeProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/discounts/:id/view",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Discounts"}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/discounts">Discounts</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>View discount</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button onClick={() => resetErrorBoundary()} size="small" danger>
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <GetDiscountCodeProvider>
          <ViewDiscountCodePage />
        </GetDiscountCodeProvider>
      </ErrorBoundary>
    ),
  },
  {
    path: "/reviews",
    element: (
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <DashboardWrapper pageTitle={"Reviews"}>
            <StyledPageHeader>Reviews</StyledPageHeader>
            <div style={{marginTop: "1.8rem"}}>
              <Alert
                message="Sorry, something went wrong."
                showIcon
                description={error.message}
                type="error"
                action={
                  <Button
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                    danger
                  >
                    Reset
                  </Button>
                }
              />
            </div>
          </DashboardWrapper>
        )}
      >
        <ReviewsPage />
      </ErrorBoundary>
    ),
  },
  {path: "/new-offering", element: <NewOfferingPage />},
];
